import React, { useEffect } from 'react';
import { Set } from 'immutable';
import {
  deleteContactFailed,
  deleteContactRequested,
  deleteContactSucceeded,
  selectContact,
} from '@repo/redux/actions/contacts';
import { selectGroup, updateGroup } from '@repo/redux/actions/groups';
import { isStarredSelector } from '@repo/redux/actions/starred';
import { store } from '@app/storeAndHistory';
import Contact from '@repo/utils/models/Contact';
import {
  ActionCopyIcon,
  PenIcon,
  ActionGroupsIcon,
  ActionMergeIcon,
  ActionArchiveIcon,
  ActionStarredIcon,
  ContactActionIcon,
  ShareActionIcon,
  ActionNoteIcon,
  ReminderAlarmClockIcon,
} from '@repo/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { openModal, startNewNote, startNewReminder } from '@repo/redux/actions/ui';
import { ID, SVGProps, UserAction, UserActionState } from '@repo/utils/types';
import { ReconnectIcon } from '@repo/icons/Reconnect';
import { removeContactsFromGroup } from './groupActions';
import { Location, NavigateFunction } from 'react-router-dom';
import analytics from '@repo/utils/helpers/analytics';
import { apiRequest } from '@repo/utils/helpers/api';
import { selectContactShareStatus } from '@repo/redux/reducers/data/shareStatuses/contacts';
import { fetchContactShareStatus } from '@repo/redux/actions/shareStatuses/contacts';
import { NexusFlowerIcon } from '@repo/icons/AIFlower';
import { clearNewConversation } from '@repo/redux/actions/nexus';
import { alertUser } from '@repo/redux/actions/alerts';
import { GetEntitlementValueFn } from '@repo/utils/context';
import { isWindows } from '@repo/utils/helpers/platform-checks';

export const toggleStarredStatus = (id: ID) => {
  const appState = store.getState();
  const starredGroup = selectGroup('starred')(appState);
  const contact = selectContact(id)(appState);
  const isAdding = !starredGroup.hasContact(id);
  const starredGroupWithUpdate = starredGroup.toggleContactInclusion(id);
  store.dispatch(
    updateGroup({
      group: starredGroupWithUpdate,
      requestPayload: starredGroup.diff(starredGroupWithUpdate),
    }),
  );
  store.dispatch(
    alertUser(
      `${contact.displayName ? contact.displayName + ' ' : ''}${
        isAdding ? 'added to' : 'removed from'
      } starred`,
      'success',
    ),
  );
};

export const archiveContact = (
  contact: Contact,
  location: Location,
  navigate: NavigateFunction,
  dispatch: Dispatch,
) => {
  dispatch(deleteContactRequested({ id: contact.id }));
  apiRequest<void>('DELETE', `/network/contacts/${contact.id}/`)
    .catch((err) => {
      dispatch(deleteContactFailed({ id: contact.id }));
      console.error(err);
    })
    .then(() => {
      analytics.track('Contact Deleted', { contactId: contact.id });
      dispatch(deleteContactSucceeded({ id: contact.id }));

      if (location.pathname.includes(contact.id)) {
        navigate(-1);
      }
    });
};

export const StarActionTitle = ({ contact }) => {
  const isStarred = useSelector(isStarredSelector(contact));
  return <>{isStarred ? 'Remove from Starred' : 'Add to Starred'}</>;
};

const ShareContactIcon = ({ contact, ...props }: SVGProps & { contact?: Contact }) => {
  const dispatch = useDispatch();
  const contactShareStatus = useSelector(selectContactShareStatus(contact?.id));

  useEffect(() => {
    if (contact && !contactShareStatus.isLoaded) {
      dispatch(fetchContactShareStatus(contact.id));
    }
  }, []);

  return <ShareActionIcon isShared={contactShareStatus.isShared} {...props} />;
};

export const buildContactActions = (
  {
    selectedContact,
    selectedContactBatch,
    groupView,
    isMultiselectView,
    location,
  }: UserActionState,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  contactIsRestricted: (contact: Contact) => boolean,
  getEntitlementValue: GetEntitlementValueFn,
  contactIsSelf: (contact: Contact) => boolean,
): Array<UserAction> => {
  if (
    !selectedContact ||
    (selectedContactBatch.size > 1 && isMultiselectView) ||
    contactIsSelf(selectedContact)
  ) {
    return [];
  }

  const isRestricted = contactIsRestricted(selectedContact);
  const canEditContact = !getEntitlementValue('contacts.edit.disabled');

  let removeFromGroup = null;
  let addToGroupLabel = 'Add to Group';

  if (!!groupView) {
    removeFromGroup = {
      title: 'Remove from Group',
      type: 'action',
      action: () => removeContactsFromGroup(groupView.group, Set([selectedContact.id]), dispatch),
      icon: ActionArchiveIcon,
      keyboard: isWindows() ? 'Backspace' : 'Del',
    };

    addToGroupLabel = 'Add to another Group';
  }

  return [
    removeFromGroup,
    {
      title: <StarActionTitle contact={selectedContact} />,
      synonyms: ['Star', 'Starred'],
      type: 'action',
      action: () => toggleStarredStatus(selectedContact.id),
      icon: (props) => <ActionStarredIcon contact={selectedContact} {...props} />,
      keyboard: 's',
    },
    !isRestricted && {
      title: 'Contact',
      type: 'action',
      action: () => dispatch(openModal('contactPerson')),
      icon: (props) => <ContactActionIcon {...props} />,
      keyboard: 'c',
      // legacy usage of Contact.information. This data is otherwise accessed via store.data.sources
      synonyms: selectedContact.isLoaded
        ? selectedContact.information.map(({ type }) => type)
        : ['email'],
    },
    !isRestricted && {
      title: 'Add Note',
      type: 'action',
      action: () => dispatch(startNewNote),
      icon: (props) => <ActionNoteIcon {...props} />,
      keyboard: 'n',
    },
    !isRestricted && {
      title: 'Set Reminder',
      type: 'action',
      action: () => dispatch(startNewReminder),
      icon: (props) => <ReminderAlarmClockIcon {...props} />,
      keyboard: 'r',
    },
    !isRestricted && {
      title: `Ask Nexus about ${selectedContact.displayName}`,
      type: 'action',
      action: () => {
        dispatch(clearNewConversation);
        navigate('/nexus', { state: { contactID: selectedContact.id } });
      },
      icon: (props) => <NexusFlowerIcon strokeWidth={1.8} {...props} />,
      synonyms: ['ai', 'chat', 'nexus'],
    },
    !isRestricted && {
      title: 'Set Reconnect Cadence',
      type: 'action',
      action: () => dispatch(openModal('reconnect')),
      icon: (props) => <ReconnectIcon {...props} contact={selectedContact} />,
      keyboard: 'h',
    },
    {
      title: addToGroupLabel,
      type: 'action',
      action: () => dispatch(openModal('addToGroups')),
      icon: ActionGroupsIcon,
      keyboard: 'L',
    },
    !isRestricted &&
      canEditContact && {
        title: 'Edit other details',
        type: 'action',
        action: () => navigate(`/contact/${selectedContact.id}/form`),
        icon: PenIcon,
      },
    selectedContact.email &&
      !isRestricted && {
        title: 'Copy email address',
        type: 'action',
        action: selectedContact.copyEmailToClipboard(),
        icon: ActionCopyIcon,
      },
    selectedContact.phone &&
      !isRestricted && {
        title: 'Copy phone number',
        type: 'action',
        action: selectedContact.copyPhoneNumberToClipboard(),
        icon: ActionCopyIcon,
      },
    {
      title: 'Merge Person',
      type: 'action',
      action: () => dispatch(openModal('merge')),
      icon: ActionMergeIcon,
      keyboard: 'm',
    },
    {
      title: 'Archive Person',
      synonyms: ['Delete', 'Remove'],
      type: 'action',
      action: () => archiveContact(selectedContact, location, navigate, dispatch),
      icon: ActionArchiveIcon,
      keyboard: '#',
    },
    !isRestricted && {
      title: 'Share Person',
      type: 'action',
      action: () => dispatch(openModal('shareContact')),
      icon: (props) => <ShareContactIcon contact={selectedContact} {...props} />,
    },
  ].filter((action) => !!action);
};
